// Circles = the evidence spectrum (fill amount = strength, gray = absence).
// Diamonds = caution states. Three redundant channels: shape + fill + color.
import type { EvidenceState } from '@/types/engineer'
import { cn } from '@/lib/utils'

interface EvidenceStateMeta {
  dotClassName: string
  badgeClassName: string
  textClassName: string
}

export const EVIDENCE_STATE_META: Record<EvidenceState, EvidenceStateMeta> = {
  'Strong Evidence': {
    dotClassName: 'size-[7px] rounded-full bg-primary',
    badgeClassName: 'bg-secondary text-primary',
    textClassName: 'text-primary',
  },
  'Some Evidence': {
    dotClassName: 'size-[7px] rounded-full bg-primary/45',
    badgeClassName: 'border border-border bg-card text-primary',
    textClassName: 'text-primary',
  },
  'Emerging Signal': {
    dotClassName: 'size-[7px] rounded-full border-[1.5px] border-primary',
    badgeClassName: 'border border-border bg-card text-foreground',
    textClassName: 'text-foreground',
  },
  'Not Yet Observed': {
    dotClassName: 'size-[7px] rounded-full border-[1.5px] border-border',
    badgeClassName: 'border border-dashed border-border text-muted-foreground',
    textClassName: 'text-muted-foreground',
  },
  'Potential Concern': {
    dotClassName: 'size-[6px] rotate-45 bg-destructive',
    badgeClassName: 'border border-destructive/40 text-destructive',
    textClassName: 'text-destructive',
  },
  'Contradictory Signal': {
    dotClassName: 'size-[6px] rotate-45 border-[1.5px] border-destructive',
    badgeClassName: 'border border-destructive/40 text-destructive',
    textClassName: 'text-destructive',
  },
}

interface EvidenceStateDotProps {
  state: EvidenceState
  className?: string
}

export function EvidenceStateDot({ state, className }: EvidenceStateDotProps) {
  return (
    <span
      className={cn('inline-block shrink-0', EVIDENCE_STATE_META[state].dotClassName, className)}
    />
  )
}

interface EvidenceStateBadgeProps {
  state: EvidenceState
  className?: string
}

export function EvidenceStateBadge({ state, className }: EvidenceStateBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-[6px] rounded-full px-[9px] py-[4px] font-mono text-[10px] uppercase tracking-[0.08em]',
        EVIDENCE_STATE_META[state].badgeClassName,
        className,
      )}
    >
      <EvidenceStateDot state={state} />
      {state}
    </span>
  )
}
